import {
	Button,
	Card,
	Col,
	notification,
	Row,
	Spin,
	Typography,
	Alert,
	Divider,
	Empty,
} from 'antd';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
	cancelSubReason,
	createClientPortalSession,
	extendClientTrialApi,
	getSubscriptionInformation,
	currentSubscriptionInformation,
	textWatermarkRemovalCheckout,
	cancelSubscription,
	userGrandfathered,
	getCoupons,
	removeDiscount,
	applyCouponCode,
} from '@/api/user';
import {
	CrownOutlined,
	CreditCardOutlined,
	DollarCircleOutlined,
	GiftOutlined,
} from '@ant-design/icons';
import { SyncAlt, Payment, StopOutlined } from '@mui/icons-material';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getLoggedInUser } from '@/store/account/selector';
import CancelSubReasonModal from '@/components/CancelSubReasonModal/CancelSubReasonModal';
import { useAppDispatch } from '@/store/hooks/useAppDispatch';
import { closeModal, openModal } from '@/store/modal/slice';
import { MODAL_IDS } from 'src/enums/EModal';
import { useState } from 'react';
import { setCurrentPage, updateUserAccount } from '@/store/account/slice';
import { useNavigate } from 'react-router-dom';
import { AppRoutes } from 'src/enums/ERoutes';
import { UserDashboardSiderItem } from 'src/enums/EUserDashboardSiderItem';
import CouponRedemption from '@/components/SubscriptionComponents/CouponRedemption';
import RenderCouponInfo from '@/components/SubscriptionComponents/RenderCouponInfo/RenderCouponInfo';

const { Title, Text } = Typography;

const ManageSubscription = () => {
	const loggedInUser = useAppSelector(getLoggedInUser);
	const [selectedSubscriptionType, setSelectedSubscriptionType] = useState<
		'Inbound' | 'Outbound' | null
	>(null);
	const [isInboundUpgradeLoading, setIsInboundUpgradeLoading] = useState(false);
	const [isOutboundUpgradeLoading, setIsOutboundUpgradeLoading] = useState(false);
	const [isInboundCancelLoading, setIsInboundCancelLoading] = useState(false);
	const [isOutboundCancelLoading, setIsOutboundCancelLoading] = useState(false);
	const dispatch = useAppDispatch();
	const navigate = useNavigate();

	// Queries
	const { data: subscriptionData, isLoading: subscriptionLoading } = useQuery({
		queryKey: ['subscription-information'],
		queryFn: () => getSubscriptionInformation('Client'),
	});

	const {
		data: couponsData,
		isLoading: couponsLoading,
		refetch: refetchCoupons,
	} = useQuery({
		queryKey: ['coupons'],
		queryFn: () => getCoupons('Client'),
		enabled: Boolean(
			(!!loggedInUser?.inboundSubscriptionId &&
				loggedInUser?.inboundSubscriptionStatus !== 'canceled') ||
				(!!loggedInUser?.outboundSubscriptionId &&
					loggedInUser?.outboundSubscriptionStatus !== 'canceled'),
		),
	});

	const { data: grandfatheredData } = useQuery({
		queryKey: ['grandFathered-data', loggedInUser.id],
		queryFn: () => userGrandfathered({ username: loggedInUser.id }),
		enabled: Boolean(
			loggedInUser?.inboundSubscriptionId &&
				loggedInUser?.inboundSubscriptionStatus !== 'canceled' &&
				!loggedInUser?.resellerClient,
		),
	});

	// Mutations
	const updatePaymentMethodMutation = useMutation({
		mutationKey: ['update-payment-method'],
		mutationFn: createClientPortalSession,
		onSuccess: (data) => {
			if (data?.status !== 'success') {
				notification.error({ message: 'Error updating payment method' });
			} else {
				notification.success({
					message: 'Redirecting to payment portal...',
				});
				window.location.href = data.redirectPaymentURL as string;
			}
		},
	});

	const CancelReasonMutation = useMutation({
		mutationKey: ['cancel-subscription-reason'],
		mutationFn: cancelSubReason,
		onSuccess: async (data, { subscriptionType }) => {
			if (!data?.success) {
				notification.error({ message: 'Error canceling subscription' });
			} else {
				await cancelSubMutation.mutateAsync(subscriptionType);
				dispatch(closeModal());
				setSelectedSubscriptionType(null);
			}
		},
		onError: () => {
			notification.error({ message: 'Error canceling subscription' });
			setSelectedSubscriptionType(null);
		},
	});

	const cancelSubMutation = useMutation({
		mutationKey: ['cancel-subscription'],
		mutationFn: cancelSubscription,
		onSuccess: (data, subscriptionType) => {
			if (!data?.success) {
				notification.error({ message: 'Error canceling subscription' });
			} else {
				notification.success({
					message: 'Subscription scheduled for cancellation',
				});
				dispatch(
					updateUserAccount({
						userAccount: {
							[subscriptionType === 'Inbound'
								? 'inboundScheduleSubCancelOn'
								: 'outboundScheduleSubCancelOn']: data?.subCancelOn,
						},
					}),
				);
			}
		},
	});

	const watermarkRemovalMutation = useMutation({
		mutationKey: ['watermark-removal'],
		mutationFn: textWatermarkRemovalCheckout,
		onSuccess: (data) => {
			if (!data?.success) {
				notification.error({ message: 'Error removing watermark' });
			} else {
				notification.success({
					message: 'Redirecting to checkout...',
				});
				window.location.href = data?.data?.url as string;
			}
		},
		onError: () => {
			notification.error({ message: 'Error removing watermark' });
		},
	});

	const upgradeSubMutation = useMutation({
		mutationKey: ['update-subscription'],
		mutationFn: createClientPortalSession,
		onSuccess: (data) => {
			if (data?.status !== 'success') {
				notification.error({ message: 'Error upgrading subscription' });
			} else {
				notification.success({
					message: 'Redirecting to subscription portal...',
				});
				window.location.href = data.redirectPaymentURL as string;
			}
		},
	});

	const currentSubscriptionInformationMutation = useMutation({
		mutationKey: ['current-subscription-information'],
		mutationFn: () => currentSubscriptionInformation('Client'),
		onSuccess: (data) => {
			dispatch(
				openModal({
					modalId: MODAL_IDS.CANCEL_REASON_MODAL,
					data: {
						...data?.data,
						subscriptionType: selectedSubscriptionType,
					},
				}),
			);
		},
		onError: () => {
			notification.error({
				message: 'Error initiating cancellation process. Please contact support.',
			});
		},
	});

	const clientExtendTrialMutation = useMutation({
		mutationKey: ['client-extend-trial'],
		mutationFn: extendClientTrialApi,
		onSuccess: (data) => {
			if (data?.status !== 'success') {
				notification.error({ message: 'Error extending trial', description: data?.error });
			} else {
				notification.success({
					message: 'Trial extended successfully',
				});
				dispatch(closeModal());
			}
		},
	});

	// Handlers
	const handleUpgrade = (type: 'Inbound' | 'Outbound') => {
		const id =
			type === 'Inbound' ? loggedInUser.inboundSubscriptionId : loggedInUser.outboundSubscriptionId;
		const status =
			type === 'Inbound'
				? loggedInUser.inboundSubscriptionStatus
				: loggedInUser.outboundSubscriptionStatus;

		if (!id || status === 'canceled') {
			notification.error({
				message: `You don't have an active ${type} subscription to upgrade. Please subscribe or contact support.`,
			});
			return;
		}
		if (type === 'Inbound') {
			setIsInboundUpgradeLoading(true);
		} else {
			setIsOutboundUpgradeLoading(true);
		}
		upgradeSubMutation.mutate(
			{ action: 'subscription_update', subscriptionType: type },
			{
				onSettled: () => {
					if (type === 'Inbound') {
						setIsInboundUpgradeLoading(false);
					} else {
						setIsOutboundUpgradeLoading(false);
					}
				},
			},
		);
	};

	const handleCancelSubscriptionType = (type: 'Inbound' | 'Outbound') => {
		const id =
			type === 'Inbound' ? loggedInUser.inboundSubscriptionId : loggedInUser.outboundSubscriptionId;
		const status =
			type === 'Inbound'
				? loggedInUser.inboundSubscriptionStatus
				: loggedInUser.outboundSubscriptionStatus;

		if (!id || status === 'canceled') {
			notification.error({
				message: `You don't have an active ${type} subscription to cancel. Please subscribe or contact support.`,
			});
			return;
		}
		setSelectedSubscriptionType(type);
		if (type === 'Inbound') {
			setIsInboundCancelLoading(true);
		} else {
			setIsOutboundCancelLoading(true);
		}
		currentSubscriptionInformationMutation.mutate(undefined, {
			onSettled: () => {
				if (type === 'Inbound') {
					setIsInboundCancelLoading(false);
				} else {
					setIsOutboundCancelLoading(false);
				}
			},
		});
	};

	const handleUpdatePaymentMethod = () => {
		updatePaymentMethodMutation.mutate({ action: 'payment_method_update', clientType: 'Client' });
	};

	const handleWatermarkRemoval = () => {
		if (
			!loggedInUser?.inboundSubscriptionId ||
			loggedInUser?.inboundSubscriptionStatus === 'canceled'
		) {
			notification.error({
				message:
					"You don't have an active subscription to remove watermark. Please subscribe or contact support.",
			});
			return;
		}
		watermarkRemovalMutation.mutate();
	};

	const handleSubscribe = () => {
		dispatch(setCurrentPage(UserDashboardSiderItem.DASHBOARD));
		navigate(AppRoutes.DASHBOARD);
	};

	// Helper function to determine if a subscription exists and is active
	const hasActiveSubscription = (type: 'Inbound' | 'Outbound') => {
		const id =
			type === 'Inbound' ? loggedInUser.inboundSubscriptionId : loggedInUser.outboundSubscriptionId;
		const status =
			type === 'Inbound'
				? loggedInUser.inboundSubscriptionStatus
				: loggedInUser.outboundSubscriptionStatus;
		return id && status !== 'canceled';
	};

	// Status badge component
	const StatusBadge = ({ status }: { status: string }) => {
		const getStatusColor = (status: string) => {
			if (status === 'active') {
				return 'bg-green-500';
			}
			if (status === 'canceled') {
				return 'bg-red-500';
			}
			return 'bg-yellow-500';
		};

		const getTextColor = (status: string) => {
			if (status === 'active') {
				return 'text-green-500';
			}
			if (status === 'canceled') {
				return 'text-red-500';
			}
			return 'text-yellow-500';
		};

		return (
			<div className="flex items-center bg-gray-50 px-4 py-2 rounded-full">
				<div className={`w-3 h-3 rounded-full ${getStatusColor(status)} mr-2`} />
				<Text strong className={`capitalize ${getTextColor(status)} text-base`}>
					{status || 'N/A'}
				</Text>
			</div>
		);
	};

	// Subscription card overlay when no subscription exists
	const SubscriptionOverlay = ({ type }: { type: 'Inbound' | 'Outbound' }) => (
		<div className="absolute inset-0 flex flex-col items-center justify-center bg-white bg-opacity-95 rounded-xl z-10 p-8">
			<Empty
				description={
					<div className="mt-4">
						<Text strong className="text-xl block mb-2">
							No {type} Subscription
						</Text>
						<Text className="text-base block mb-6">
							Subscribe now to get started with our services
						</Text>
					</div>
				}
				image={Empty.PRESENTED_IMAGE_SIMPLE}
				className="mb-6"
			/>
			<Button type="primary" size="large" className="px-8" onClick={handleSubscribe}>
				Subscribe Now
			</Button>
		</div>
	);

	return (
		<div className="w-full p-4 md:p-8">
			{/* Header Section */}
			<div className="mb-12">
				<div className="flex flex-col md:flex-row md:items-center justify-between">
					<div className="flex items-center mb-4 md:mb-0">
						<div className="bg-blue-50 p-4 rounded-full mr-5 shadow-sm">
							<CrownOutlined className="text-3xl text-blue-500" />
						</div>
						<div>
							<Title level={1} className="!mb-1 !text-3xl">
								Subscription Management
							</Title>
							<Text type="secondary" className="text-lg">
								Manage your subscription plans and billing preferences
							</Text>
						</div>
					</div>
				</div>
			</div>

			{/* Grandfathered Alert */}
			{grandfatheredData?.data?.grandfathered && (
				<Alert
					className="my-8 text-lg"
					type="info"
					closable
					showIcon
					description={
						<span className="font-normal">
							⭐ You are grandfathered! You are paying ${grandfatheredData?.data?.oldAmount}/
							{grandfatheredData?.data?.interval} for {grandfatheredData?.data?.name} while it costs
							${grandfatheredData?.data?.newAmount}/{grandfatheredData?.data?.interval} now.
						</span>
					}
				/>
			)}

			<Spin spinning={subscriptionLoading || couponsLoading}>
				<div className="space-y-12">
					{/* Subscription Cards */}
					<Card
						className="shadow-lg rounded-xl overflow-hidden border-0"
						title={
							<div className="flex items-center p-4">
								<DollarCircleOutlined className="text-green-500 text-xl mr-3" />
								<Text strong className="text-xl">
									Your Subscriptions
								</Text>
							</div>
						}
						bodyStyle={{ padding: '32px' }}
					>
						<Row gutter={[32, 32]}>
							{/* Inbound Plan Card */}
							<Col xs={24} lg={12}>
								<div className="relative h-full">
									<Card
										className="h-full bg-gradient-to-br from-blue-50 to-white shadow-lg hover:shadow-xl transition-all duration-300 rounded-xl border-0"
										bodyStyle={{ padding: '32px' }}
									>
										<div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
											<div className="flex items-center mb-4 md:mb-0">
												<div className="bg-blue-100 p-3 rounded-full">
													<CrownOutlined className="text-blue-600 text-2xl" />
												</div>
												<Text strong className="ml-3 text-xl">
													Inbound Subscription
												</Text>
											</div>
											{subscriptionData?.data?.inboundPlanStatus && (
												<StatusBadge status={subscriptionData?.data?.inboundPlanStatus} />
											)}
										</div>

										<Divider className="my-5" />

										<div className="mb-8">
											<Text type="secondary" className="block mb-2 text-lg">
												Plan
											</Text>
											<Text strong className="text-xl">
												{subscriptionData?.data?.inboundPlan || 'No Plan Selected'}{' '}
												{subscriptionData?.data?.inboundSubQuantity &&
												subscriptionData?.data?.inboundSubQuantity > 1
													? 'x ' + subscriptionData?.data?.inboundSubQuantity
													: null}
											</Text>
										</div>

										<div className="mb-8">
											<Text type="secondary" className="block mb-2 text-lg">
												Billing Cycle
											</Text>
											<Text className="text-lg">
												{subscriptionData?.data?.inboundSubscriptionInterval || 'N/A'}
											</Text>
										</div>

										<div className="flex flex-col md:flex-row md:justify-end space-y-3 md:space-y-0 md:space-x-4 mt-8">
											<Button
												type="primary"
												size="large"
												className="rounded-lg"
												onClick={() => handleUpgrade('Inbound')}
												loading={isInboundUpgradeLoading}
												disabled={!hasActiveSubscription('Inbound') || isInboundCancelLoading}
											>
												Upgrade
											</Button>
											<Button
												danger
												size="large"
												className="rounded-lg"
												onClick={() => handleCancelSubscriptionType('Inbound')}
												loading={isInboundCancelLoading}
												disabled={
													!hasActiveSubscription('Inbound') ||
													isInboundUpgradeLoading ||
													!!loggedInUser.inboundScheduleSubCancelOn
												}
											>
												Cancel
											</Button>
										</div>
									</Card>

									{!hasActiveSubscription('Inbound') && <SubscriptionOverlay type="Inbound" />}
								</div>
							</Col>

							{/* Outbound Plan Card */}
							<Col xs={24} lg={12}>
								<div className="relative h-full">
									<Card
										className="h-full bg-gradient-to-br from-purple-50 to-white shadow-lg hover:shadow-xl transition-all duration-300 rounded-xl border-0"
										bodyStyle={{ padding: '32px' }}
									>
										<div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
											<div className="flex items-center mb-4 md:mb-0">
												<div className="bg-purple-100 p-3 rounded-full">
													<SyncAlt className="text-purple-600 text-2xl" />
												</div>
												<Text strong className="ml-3 text-xl">
													Outbound Subscription
												</Text>
											</div>
											{subscriptionData?.data?.outboundPlanStatus && (
												<StatusBadge status={subscriptionData?.data?.outboundPlanStatus} />
											)}
										</div>

										<Divider className="my-5" />

										<div className="mb-8">
											<Text type="secondary" className="block mb-2 text-lg">
												Plan
											</Text>
											<Text strong className="text-xl">
												{subscriptionData?.data?.outboundPlan || 'No Plan Selected'}{' '}
												{subscriptionData?.data?.outboundSubQuantity &&
												subscriptionData?.data?.outboundSubQuantity > 1
													? 'x ' + subscriptionData?.data?.outboundSubQuantity
													: null}
											</Text>
										</div>

										<div className="mb-8">
											<Text type="secondary" className="block mb-2 text-lg">
												Billing Cycle
											</Text>
											<Text className="text-lg">
												{subscriptionData?.data?.outboundSubscriptionInterval || 'N/A'}
											</Text>
										</div>

										<div className="flex flex-col md:flex-row md:justify-end space-y-3 md:space-y-0 md:space-x-4 mt-8">
											{/* <Button
                        type='primary'
                        size='large'
                        className='rounded-lg'
                        onClick={() => handleUpgrade('Outbound')}
                        loading={upgradeSubMutation.isPending}
                        disabled={!hasActiveSubscription('Outbound')}
                      >
                        Upgrade
                      </Button> */}
											<Button
												danger
												size="large"
												className="rounded-lg"
												onClick={() => handleCancelSubscriptionType('Outbound')}
												loading={isOutboundCancelLoading}
												disabled={
													!hasActiveSubscription('Outbound') ||
													isOutboundUpgradeLoading ||
													!!loggedInUser.outboundScheduleSubCancelOn
												}
											>
												Cancel
											</Button>
										</div>
									</Card>

									{!hasActiveSubscription('Outbound') && <SubscriptionOverlay type="Outbound" />}
								</div>
							</Col>
						</Row>
					</Card>

					{/* Promotional Credits and Billing & Payment Section */}
					<Row gutter={[32, 32]}>
						{/* Billing & Payment */}
						<Col xs={24} lg={12}>
							<Card
								className="shadow-lg rounded-xl overflow-hidden border-0 h-full"
								title={
									<div className="flex items-center p-4">
										<CreditCardOutlined className="text-blue-500 text-xl mr-3" />
										<Text strong className="text-xl">
											Billing & Payment
										</Text>
									</div>
								}
								bodyStyle={{ padding: '32px' }}
							>
								<Row gutter={[32, 32]}>
									{/* Update Payment Method */}
									<Col xs={24}>
										<Card
											className="h-full bg-white shadow-md hover:shadow-lg transition-all duration-300 rounded-xl border border-gray-100"
											bodyStyle={{ padding: '32px' }}
										>
											<div className="flex flex-col md:flex-row md:items-center mb-6">
												<div className="bg-blue-50 p-4 rounded-full mb-4 md:mb-0 md:mr-4">
													<Payment className="text-blue-500 text-2xl" />
												</div>
												<div>
													<Text strong className="block text-xl mb-2">
														Payment Method
													</Text>
													<Text type="secondary" className="text-lg">
														Update your card or payment details
													</Text>
												</div>
											</div>
											<Button
												type="primary"
												size="large"
												className="mt-6 w-full md:w-auto"
												onClick={handleUpdatePaymentMethod}
												loading={updatePaymentMethodMutation.isPending}
											>
												Update Payment Method
											</Button>
										</Card>
									</Col>

									{/* Watermark Removal - Only show if needed */}
									{hasActiveSubscription('Inbound') && loggedInUser?.showWaterMarkRemoval && (
										<Col xs={24}>
											<Card
												className="h-full bg-white shadow-md hover:shadow-lg transition-all duration-300 rounded-xl border border-gray-100"
												bodyStyle={{ padding: '32px' }}
											>
												<div className="flex flex-col md:flex-row md:items-center mb-6">
													<div className="bg-red-50 p-4 rounded-full mb-4 md:mb-0 md:mr-4">
														<StopOutlined className="text-red-500 text-2xl" />
													</div>
													<div>
														<Text strong className="block text-xl mb-2">
															Watermark Settings
														</Text>
														<Text type="secondary" className="text-lg">
															Remove watermark from texting workflow
														</Text>
													</div>
												</div>
												{loggedInUser?.removeWatermark ? (
													<Alert
														message="Watermark has been removed"
														type="success"
														showIcon
														className="mt-6"
													/>
												) : (
													<Button
														danger
														size="large"
														className="mt-6 w-full md:w-auto"
														onClick={handleWatermarkRemoval}
														loading={watermarkRemovalMutation.isPending}
													>
														Remove Watermark
													</Button>
												)}
											</Card>
										</Col>
									)}
								</Row>
							</Card>
						</Col>

						{/* Promotional Credits */}
						<Col xs={24} lg={12}>
							<Card
								className="shadow-lg rounded-xl overflow-hidden border-0 h-full"
								title={
									<div className="flex items-center p-4">
										<GiftOutlined className="text-purple-500 text-xl mr-3" />
										<Text strong className="text-xl">
											Promotional Credits
										</Text>
									</div>
								}
								bodyStyle={{ padding: '32px' }}
							>
								<div className="space-y-4">
									<div className="bg-gray-50 p-4 rounded-lg">
										<RenderCouponInfo
											appliedCoupons={couponsData?.data?.appliedCoupons as any}
											onUpdate={refetchCoupons}
											removeDiscount={removeDiscount}
										/>
									</div>

									<div className="mt-6">
										<CouponRedemption
											updateSuccess={refetchCoupons}
											applyCouponCode={applyCouponCode}
										/>
									</div>
								</div>
							</Card>
						</Col>
					</Row>
				</div>
			</Spin>

			{/* Modals */}
			<CancelSubReasonModal
				mutation={CancelReasonMutation}
				onExtendTrial={() =>
					clientExtendTrialMutation.mutate(loggedInUser?.inboundSubscriptionId as string)
				}
				isExtendingTrial={clientExtendTrialMutation.isPending}
				loading={cancelSubMutation.isPending || CancelReasonMutation.isPending}
				showCoupon={true}
			/>
			{/* <PlansModal /> */}
		</div>
	);
};

export default ManageSubscription;
